import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import { extractLeetCodeUsername } from "../../../utils/leetcode";
import { getCourseCompleted, getCourseTotalQuestions, hasCourseBreakdownData } from "../Helpers/clusterCourseMetrics.js";

/** react-pdf + single A4 page overflow breaks PDF blob generation; keep rows per page conservative. */
const ROWS_PER_PAGE_AGGREGATE = 18;
const ROWS_PER_PAGE_PER_COURSE = 12;

function chunkStudents(list, size) {
  if (!list?.length) return [[]];
  const pages = [];
  for (let i = 0; i < list.length; i += size) {
    pages.push(list.slice(i, i + size));
  }
  return pages;
}

/** Avoid Invalid Date / non-ISO values breaking @react-pdf Text layout. */
function pdfFormatDate(value) {
  if (value == null || value === "") return "-";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    const day = String(d.getUTCDate()).padStart(2, "0");
    const month = String(d.getUTCMonth() + 1).padStart(2, "0");
    const year = d.getUTCFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return "-";
  }
}

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: "Helvetica",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#bfbfbf",
    paddingBottom: 10,
    marginBottom: 20,
  },
  brand: {
    width: 72,
    height: 72,
    borderWidth: 1,
    borderColor: "#bfbfbf",
    borderRadius: 8,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#f4f6f8",
  },
  brandText: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#5c7a94",
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#333",
  },
  subtitle: {
    fontSize: 12,
    color: "#666",
  },
  continued: {
    fontSize: 11,
    color: "#666",
    marginBottom: 12,
  },
  table: {
    width: "100%",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#bfbfbf",
    marginTop: 10,
    marginBottom: 10,
  },
  tableRow: {
    flexDirection: "row",
    backgroundColor: "#f7f7f7",
    borderBottomColor: "#bfbfbf",
    borderBottomWidth: 1,
  },
  tableRowOdd: {
    backgroundColor: "#ffffff",
  },
  tableCol: {
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#bfbfbf",
  },
  tableCell: {
    marginTop: 5,
    marginBottom: 5,
    paddingHorizontal: 4,
    fontSize: 9,
    textAlign: "center",
  },
  tableCellCourses: {
    marginTop: 4,
    marginBottom: 4,
    paddingHorizontal: 5,
    fontSize: 7,
    textAlign: "left",
  },
  tableHeader: {
    backgroundColor: "#e0e0e0",
    fontWeight: "bold",
  },
  footer: {
    marginTop: 20,
    textAlign: "center",
    fontSize: 10,
    color: "#666",
  },
});

const PDFDocument = ({
  students,
  courses,
  ClusterName,
  CreatedAt,
  leetcodeBulkTotals,
  leetcodeBulkByStudentId,
  includeLeetcodeColumn = false,
}) => {
  const coursesList = Array.isArray(courses) ? courses : [];
  const hasPerCourse = coursesList.length > 0;

  const w = hasPerCourse
    ? includeLeetcodeColumn
      ? { email: "15%", name: "9%", dept: "7%", pts: "6%", last: "10%", cq: "27%", lc: "16%" }
      : { email: "17%", name: "10%", dept: "8%", pts: "7%", last: "12%", cq: "46%" }
    : includeLeetcodeColumn
      ? { email: "22%", name: "12%", dept: "10%", pts: "8%", last: "16%", cq: "16%", lc: "16%" }
      : { email: "24%", name: "14%", dept: "12%", pts: "10%", last: "20%", cq: "20%" };

  const list = students ?? [];
  const rowsPerPage = hasPerCourse ? ROWS_PER_PAGE_PER_COURSE : ROWS_PER_PAGE_AGGREGATE;
  const pages = chunkStudents(list, rowsPerPage);
  const totalPages = Math.max(pages.length, 1);

  const courseProgressText = (student) =>
    coursesList
      .map((course) => {
        const label = String(course?.name || "Course")
          .replace(/\r?\n/g, " ")
          .trim();
        const done = getCourseCompleted(course, student);
        const tot = getCourseTotalQuestions(course);
        const hasBreakdownData = hasCourseBreakdownData(course, student);
        return `${label}: ${hasBreakdownData ? `${done}/${tot || "—"}` : done}`;
      })
      .join("\n");

  const renderTableHeader = () => (
    <View style={[styles.tableRow, styles.tableHeader]}>
      <View style={[styles.tableCol, { width: w.email }]}>
        <Text style={styles.tableCell}>Email</Text>
      </View>
      <View style={[styles.tableCol, { width: w.name }]}>
        <Text style={styles.tableCell}>Name</Text>
      </View>
      <View style={[styles.tableCol, { width: w.dept }]}>
        <Text style={styles.tableCell}>Department</Text>
      </View>
      <View style={[styles.tableCol, { width: w.pts }]}>
        <Text style={styles.tableCell}>Points</Text>
      </View>
      <View style={[styles.tableCol, { width: w.last }]}>
        <Text style={styles.tableCell}>Course Last Submission</Text>
      </View>
      <View style={[styles.tableCol, { width: w.cq }]}>
        <Text style={hasPerCourse ? styles.tableCellCourses : styles.tableCell}>
          {hasPerCourse ? "Better Tomorrow courses\n(completed / total)" : "Course Questions Completed"}
        </Text>
      </View>
      {includeLeetcodeColumn && (
        <View style={[styles.tableCol, { width: w.lc }]}>
          <Text style={styles.tableCell}>Leetcode Questions Completed</Text>
        </View>
      )}
    </View>
  );

  const renderStudentRow = (student, index) => {
    if (!student?.student) {
      return (
        <View
          style={[styles.tableRow, index % 2 === 0 ? styles.tableRowOdd : null]}
          key={`missing-${index}`}
        >
          <View style={[styles.tableCol, { width: "100%" }]}>
            <Text style={styles.tableCell}>-</Text>
          </View>
        </View>
      );
    }

    let leetcodeTotal = null;
    if (includeLeetcodeColumn) {
      let leetcodeEasy = 0;
      let leetcodeMedium = 0;
      let leetcodeHard = 0;
      if (student.leetcode_data && student.leetcode_data.length > 0) {
        leetcodeEasy = student.leetcode_data.find((data) => data.difficulty === "Easy")?.count || 0;
        leetcodeMedium = student.leetcode_data.find((data) => data.difficulty === "Medium")?.count || 0;
        leetcodeHard = student.leetcode_data.find((data) => data.difficulty === "Hard")?.count || 0;
      }
      const fromLeaderboard = leetcodeEasy + leetcodeMedium + leetcodeHard;
      const sid =
        student?.student?.id != null && student.student.id !== ""
          ? String(student.student.id)
          : student?.student_id != null && student.student_id !== ""
            ? String(student.student_id)
            : null;
      let fromBulk = null;
      if (leetcodeBulkByStudentId && sid && typeof leetcodeBulkByStudentId[sid] === "number") {
        fromBulk = leetcodeBulkByStudentId[sid];
      } else if (leetcodeBulkTotals) {
        const extracted = extractLeetCodeUsername(
          String(student?.student?.leetCodeUname ?? "")
        );
        const uname = extracted?.trim().toLowerCase();
        if (uname && typeof leetcodeBulkTotals[uname] === "number") {
          fromBulk = leetcodeBulkTotals[uname];
        }
      }
      leetcodeTotal = fromBulk != null ? fromBulk : fromLeaderboard;
    }

    const name =
      student.student?.name && String(student.student.name).trim() !== ""
        ? String(student.student.name)
        : "-";
    const dept =
      student.department && String(student.department).trim() !== ""
        ? String(student.department)
        : "-";

    return (
      <View
        style={[styles.tableRow, index % 2 === 0 ? styles.tableRowOdd : null]}
        key={`${student?.student?.id ?? student?.student_id ?? student?.student?.email ?? "r"}-${index}`}
      >
        <View style={[styles.tableCol, { width: w.email }]}>
          <Text style={styles.tableCell}>{String(student.student?.email ?? "-")}</Text>
        </View>
        <View style={[styles.tableCol, { width: w.name }]}>
          <Text style={styles.tableCell}>{name}</Text>
        </View>
        <View style={[styles.tableCol, { width: w.dept }]}>
          <Text style={styles.tableCell}>{dept}</Text>
        </View>
        <View style={[styles.tableCol, { width: w.pts }]}>
          <Text style={styles.tableCell}>{String(student.totalScore ?? "-")}</Text>
        </View>
        <View style={[styles.tableCol, { width: w.last }]}>
          <Text style={styles.tableCell}>{pdfFormatDate(student.latestSubmissionDate)}</Text>
        </View>
        <View style={[styles.tableCol, { width: w.cq }]}>
          <Text style={hasPerCourse ? styles.tableCellCourses : styles.tableCell} wrap>
            {hasPerCourse ? courseProgressText(student) : String(student.completedQuestions ?? "-")}
          </Text>
        </View>
        {includeLeetcodeColumn && (
          <View style={[styles.tableCol, { width: w.lc }]}>
            <Text style={styles.tableCell}>{String(leetcodeTotal ?? "-")}</Text>
          </View>
        )}
      </View>
    );
  };

  return (
    <Document>
      {pages.map((pageRows, pageIndex) => (
        <Page key={pageIndex} size="A4" style={styles.page}>
          {pageIndex === 0 ? (
            <View style={styles.header}>
              <View style={styles.brand}>
                <Text style={styles.brandText}>BT</Text>
              </View>
              <View>
                <Text style={styles.title}>{String(ClusterName ?? "Cluster")} Report</Text>
                <Text style={styles.subtitle}>Created At: {String(CreatedAt ?? "-")}</Text>
              </View>
            </View>
          ) : (
            <Text style={styles.continued}>
              {String(ClusterName ?? "Cluster")} Report (continued) - page {pageIndex + 1} of {totalPages}
            </Text>
          )}

          <View style={styles.table}>
            {renderTableHeader()}
            {pageRows.length === 0 ? (
              <View style={[styles.tableRow, styles.tableRowOdd]}>
                <View style={[styles.tableCol, { width: "100%" }]}>
                  <Text style={styles.tableCell}>No data</Text>
                </View>
              </View>
            ) : (
              pageRows.map((student, i) => renderStudentRow(student, pageIndex * rowsPerPage + i))
            )}
          </View>

          <Text style={styles.footer}>
            Generated by Better Tomorrow
            {totalPages > 1 ? ` - Page ${pageIndex + 1}/${totalPages}` : ""}
          </Text>
        </Page>
      ))}
    </Document>
  );
};

export { PDFDocument };
